import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { NetworkHelper } from '../helpers';
import { SHARED_ENV } from '../tokens';

/**
 * Import of the monthly custody fees 1C calculates.
 *
 * 1C exports a fee workbook (`Brokerage&custody&TM_MMYY.xlsx`) with one row per
 * client per instrument held. The backend reads the `Custody` rows commented
 * `Хранение <ISIN> dd.mm.yyyy - dd.mm.yyyy`, groups them by brokerage account
 * and matches each account to a user. The desk picks which clients to charge
 * and the run debits every row as a `custody_fee` ledger in the row's own
 * currency.
 *
 *  - **This takes money off clients and there is no undo.** Reversing a charge
 *    means an adjustment per ledger.
 *  - **A re-import is safe.** Every row is keyed on account + instrument +
 *    currency + period behind a unique index, so a row already charged is
 *    reported as `already-charged` rather than debited twice.
 *  - **The run re-reads the file.** Amounts always come from the workbook, so
 *    run sends the same base64 back together with the sha256 parse returned.
 *
 * Backend: `trading-be-1/docs/custody-fee-import.md`.
 */

/** Terminal statuses plus `running`, which also covers a crashed run. */
export type CustodyFeeRunStatus =
  | 'running'
  | 'completed'
  | 'completed_with_errors'
  | 'failed';

/** A client cannot be selected while it carries one of these. */
export type CustodyFeeBlockerCode =
  | 'USER_NOT_FOUND'
  | 'AMBIGUOUS_ACCOUNT'
  | 'KYC_NOT_APPROVED'
  | 'UNKNOWN_CURRENCY'
  | 'ALL_ALREADY_CHARGED';

export type CustodyFeeWarningCode =
  | 'SOME_ALREADY_CHARGED'
  | 'NEGATIVE_BALANCE_AFTER'
  | 'MULTIPLE_CURRENCIES';

export type CustodyFeeIssueCode = CustodyFeeBlockerCode | CustodyFeeWarningCode;

// --- parse -----------------------------------------------------------------

export interface ParseCustodyFeePayload {
  /** Base64 of the .xlsx. There is no multipart endpoint anywhere in this app. */
  file: string;
  fileName: string;
}

export interface CustodyFeeSourceFile {
  name: string | null;
  sha256: string | null;
  rowCount: number | null;
}

/** One workbook row: one instrument held by one client for one period. */
export interface CustodyFeeImportLine {
  /** 1-based, as Excel shows it. */
  rowNumber: number;
  client: string;
  account: string;
  currency: string;
  /** Decimal strings, as 1C wrote them — render, do not compute. */
  amount: string;
  amountAmd: string | null;
  date: string | null;
  /** Usually an ISIN; sometimes several, comma-separated, or a name. */
  instrument: string;
  periodStart: string;
  periodEnd: string;
  /** YYYY-MM the charge is filed under. */
  period: string;
  residency: string | null;
  comment: string;
  manualRef: string;
  alreadyCharged: boolean;
  existingLedgerId: string | null;
  existingStatus: string | null;
}

/** One brokerage account in the file, matched to a user when possible. */
export interface CustodyFeeImportUser {
  account: string;
  /** The client name as 1C wrote it. */
  clientName: string | null;
  residency: string | null;
  userId: string | null;
  userName: string | null;
  email: string | null;
  kycStatus: string | null;
  syncsWith1C: boolean | null;
  matchedUserIds: string[];
  currency: string;
  /** Everything billed in the file, and what is still to charge. */
  total: string;
  pendingAmount: string;
  lineCount: number;
  pendingCount: number;
  currentBalance: string | null;
  balanceAfter: string | null;
  blockers: CustodyFeeBlockerCode[];
  warnings: CustodyFeeWarningCode[];
  selectable: boolean;
  lines: CustodyFeeImportLine[];
}

export interface CustodyFeeCurrencyTotal {
  currency: string;
  amount: string;
  /** Present on a run's totals: lines the desk left out. */
  excludedAmount?: string;
  /** Present on a run's totals: what was actually debited. */
  chargedAmount?: string;
}

export interface CustodyFeeSkippedRow {
  rowNumber: number;
  account: string;
  reason: string;
  comment: string;
}

export interface CustodyFeeParseResponse {
  sourceFile: CustodyFeeSourceFile;
  sheetName: string;
  period: string | null;
  /** Lines per billing month — normally one entry. */
  periods: Record<string, number>;
  users: CustodyFeeImportUser[];
  summary: {
    users: number;
    selectable: number;
    blocked: number;
    lines: number;
    alreadyCharged: number;
    blockerCounts: Record<string, number>;
  };
  totals: CustodyFeeCurrencyTotal[];
  /** What would be charged if every selectable client were picked. */
  chargeableTotals: CustodyFeeCurrencyTotal[];
  /** Rows read and deliberately left out, counted per service type. */
  ignored: Record<string, number>;
  /** Custody rows that could not be charged, with a reason. */
  skipped: CustodyFeeSkippedRow[];
}

// --- run -------------------------------------------------------------------

export interface RunCustodyFeePayload {
  file: string;
  fileName?: string;
  /** From the parse response — refuses the run if the file changed. */
  fileSha256: string;
  userIds: string[];
  /**
   * `manualRef`s of single lines to leave out of a selected client's charge.
   * Each must belong to a selected client, and a client cannot have every
   * line excluded — untick the client instead.
   */
  excludedLineRefs?: string[];
  /** Required. Without it nothing happens. */
  confirm: true;
  /** Off by default: 1C calculated these fees, so pushing them back double-counts. */
  syncTo1C?: boolean;
  note?: string;
  /**
   * ISO date-time with offset to book every ledger on (what the client sees as
   * the transaction date). Omit for the processing time. Not in the future,
   * not more than 366 days back.
   */
  valueDate?: string;
}

export interface CustodyFeeRunCounts {
  users: number;
  lines: number;
  charged: number;
  skipped: number;
  failed: number;
  /** Lines the desk left out. */
  excluded?: number;
}

export type CustodyFeeUserOutcome =
  | 'pending'
  | 'charged'
  | 'partial'
  | 'skipped'
  | 'failed';

export interface CustodyFeeRunUser {
  userId: string;
  email?: string | null;
  account: string;
  clientName?: string | null;
  currency: string;
  amount: string;
  excludedAmount?: string;
  chargedAmount: string;
  lineCount: number;
  charged: number;
  skipped: number;
  failed: number;
  excluded?: number;
  status: CustodyFeeUserOutcome;
}

/** A line the desk left out of a client's charge. No ledger was posted for it. */
export interface CustodyFeeExcludedLine {
  userId: string;
  account: string;
  instrument: string;
  rowNumber: number;
  currency: string;
  amount: string;
  periodStart: string;
  periodEnd: string;
  manualRef: string;
}

/** `already-charged` or `kyc-not-approved` for a skip; an error message for a failure. */
export interface CustodyFeeFailure {
  userId: string;
  account: string;
  instrument: string;
  rowNumber: number;
  status: 'skipped' | 'failed';
  reason?: string;
}

export interface CustodyFeeRun {
  id: string;
  status: CustodyFeeRunStatus;
  period: string | null;
  adminId?: string;
  adminEmail?: string | null;
  note?: string | null;
  syncTo1C?: boolean;
  /** The instant every ledger was booked on, or null for the processing time. */
  valueDate?: string | null;
  sourceFile?: CustodyFeeSourceFile;
  startedAt?: string | number;
  finishedAt?: string | number;
  counts?: CustodyFeeRunCounts;
  totals?: CustodyFeeCurrencyTotal[];
  error?: string | null;
  /** Detail endpoint only. */
  users?: CustodyFeeRunUser[];
  failures?: CustodyFeeFailure[];
  excludedLines?: CustodyFeeExcludedLine[];
}

/** What an imported custody fee ledger carries in `details`. */
export interface CustodyFeeChargeDetails {
  type?: string;
  origin?: string;
  period?: string;
  periodStart?: string;
  periodEnd?: string;
  instrument?: string;
  account?: string;
  rowNumber?: number;
  amount?: string;
  amountAmd?: string | null;
  comment?: string;
  runId?: string;
  [key: string]: unknown;
}

/** One debit: the ledger as the transaction feed renders it, plus the email. */
export interface CustodyFeeCharge {
  id?: string;
  ledgerId?: string;
  userId?: string;
  email?: string;
  currency?: string;
  /** Negative, as a debit. */
  quantity?: string | number;
  status?: string;
  created?: string | number;
  source?: string;
  description?: string;
  details?: CustodyFeeChargeDetails;
}

export interface CustodyFeeChargeParams {
  page: number;
  itemsPerPage: number;
  userId?: string;
}

export interface CustodyFeeChargeResponse {
  items: CustodyFeeCharge[];
  total: number;
}

export interface CustodyFeeListParams {
  page: number;
  itemsPerPage: number;
  status?: string;
  /** YYYY-MM */
  period?: string;
}

export interface CustodyFeeListResponse {
  items: CustodyFeeRun[];
  total: number;
}

@Injectable({ providedIn: 'root' })
export class CustodyFeeService {
  private readonly env = inject(SHARED_ENV);
  private readonly networkHelper = inject(NetworkHelper);
  /** Already ends in `/api`. */
  private readonly baseUrl: string;

  constructor() {
    this.baseUrl = `${this.env.host}/admin/custody-fee-imports`;
  }

  /** Nothing is persisted. Re-upload as often as you like. */
  parse(payload: ParseCustodyFeePayload): Observable<CustodyFeeParseResponse> {
    return this.networkHelper.post(`${this.baseUrl}/parse`, payload);
  }

  /** Irreversible. */
  run(payload: RunCustodyFeePayload): Observable<CustodyFeeRun> {
    return this.networkHelper.post(this.baseUrl, payload);
  }

  list(params: CustodyFeeListParams): Observable<CustodyFeeListResponse> {
    const query: Record<string, string | number> = {
      page: params.page,
      itemsPerPage: params.itemsPerPage,
    };
    if (params.status) query['status'] = params.status;
    if (params.period) query['period'] = params.period;
    return this.networkHelper.get(this.baseUrl, { ...query });
  }

  /** The run with its per-client results and capped failures. */
  getById(id: string): Observable<CustodyFeeRun> {
    return this.networkHelper.get(`${this.baseUrl}/${encodeURIComponent(id)}`);
  }

  /** Every ledger the run posted. */
  listCharges(
    id: string,
    params: CustodyFeeChargeParams
  ): Observable<CustodyFeeChargeResponse> {
    const query: Record<string, string | number> = {
      page: params.page,
      itemsPerPage: params.itemsPerPage,
    };
    if (params.userId) query['userId'] = params.userId;
    return this.networkHelper.get(
      `${this.baseUrl}/${encodeURIComponent(id)}/charges`,
      { ...query }
    );
  }
}
